/**
 * BreathingOrb.jsx — Reclaim Premium Suite · Signature Moment #1
 *
 * A central, slowly breathing radial gradient orb that lives BEHIND the
 * UI of the Private Suite. Pulses at the frequency of the chosen brain
 * state (alpha ≈ 10 Hz visualised as ~6-8s cycle, theta slower, delta
 * slowest, beta faster). Honours prefers-reduced-motion.
 *
 * Sits absolute-positioned in a parent with `relative` and `overflow:
 * hidden`. Pointer-events: none so it never blocks input. Z-index 0.
 *
 * Use:
 *   <div className="relative overflow-hidden">
 *     <BreathingOrb brainState="alpha" />
 *     <YourContent />
 *   </div>
 */
import React from "react";
import { motion, useReducedMotion } from "framer-motion";

const CYCLES = {
  beta:  4.0,    // alert / focused
  alpha: 6.5,    // calm / relaxed
  theta: 9.0,    // meditative
  delta: 12.0,   // sleep
  none:  6.5,
};

export default function BreathingOrb({
  brainState = "alpha",
  tone = "gold",        // "gold" | "ember" | "mist"
  size = 720,
  className = "",
  testid = "breathing-orb",
}) {
  const reduce = useReducedMotion();
  const dur = CYCLES[brainState] ?? CYCLES.alpha;

  const GOLD = {
    stop1: "rgba(215, 187, 122, 0.32)",
    stop2: "rgba(168, 138, 76, 0.14)",
    stop3: "rgba(7, 13, 11, 0.0)",
  };
  const palette = {
    gold: GOLD,
    ember: {
      stop1: "rgba(255, 77, 41, 0.28)",
      stop2: "rgba(255, 110, 60, 0.12)",
      stop3: "rgba(7, 13, 11, 0.0)",
    },
    mist: {
      stop1: "rgba(180, 200, 190, 0.22)",
      stop2: "rgba(80, 120, 100, 0.10)",
      stop3: "rgba(7, 13, 11, 0.0)",
    },
  }[tone] || GOLD;

  return (
    <motion.div
      aria-hidden="true"
      data-testid={testid}
      className={`pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 ${className}`}
      style={{
        left: "50%",
        top: "50%",
        width: size,
        height: size,
        zIndex: 0,
        background: `radial-gradient(circle at center,
          ${palette.stop1} 0%,
          ${palette.stop2} 32%,
          ${palette.stop3} 72%)`,
        filter: "blur(8px)",
      }}
      animate={reduce ? undefined : { scale: [1.0, 1.08, 1.0], opacity: [0.78, 1.0, 0.78] }}
      transition={reduce ? undefined : {
        duration: dur,
        repeat: Infinity,
        ease: [0.45, 0, 0.55, 1],
      }}
    />
  );
}
